import { useSearchParams } from "react-router-dom";
import Icon from "../components/Icon";
import CarCard from "../components/CarCard";
import CarGridState from "../components/CarGridState";
import CountUp from "../components/CountUp";
import { carStatus, formatPrice, PRICE_CAPS } from "../lib/format";
import useDocumentTitle from "../lib/useDocumentTitle";
import s from "./Inventory.module.css";

const SORTS = {
  doporucene: { label: "Doporučené", fn: (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) },
  "cena-vzestupne": { label: "Cena: od nejnižší", fn: (a, b) => a.price - b.price },
  "cena-sestupne": { label: "Cena: od nejvyšší", fn: (a, b) => b.price - a.price },
  nejnovejsi: { label: "Rok výroby: nejnovější", fn: (a, b) => Number(b.year) - Number(a.year) },
  najezd: { label: "Nejnižší nájezd", fn: (a, b) => a.mileage - b.mileage },
};

export default function Inventory({ cars, carsStatus }) {
  useDocumentTitle("Skladové elektromobily");
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const brand = params.get("znacka") || "";
  const sort = SORTS[params.get("razeni")] ? params.get("razeni") : "doporucene";
  const max = Number(params.get("max")) || 0;
  const hideSold = params.get("prodane") !== "zobrazit";

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const brands = [...new Set(cars.map((c) => c.brand))].sort((a, b) => a.localeCompare(b, "cs"));
  const query = q.trim().toLowerCase();
  const filtered = cars
    .filter((c) =>
      (!brand || c.brand === brand) &&
      (!query || `${c.brand} ${c.model} ${c.year}`.toLowerCase().includes(query)) &&
      (!max || c.price <= max) &&
      (!hideSold || carStatus(c) !== "sold"))
    .sort(SORTS[sort].fn);
  const soldCount = cars.filter((c) => carStatus(c) === "sold").length;

  // Header facts, computed from the actual stock (nothing hardcoded).
  const onOffer = cars.filter((c) => carStatus(c) !== "sold");
  const sohValues = onOffer.map((c) => parseFloat(String(c.soh).replace(",", "."))).filter((n) => n > 0);
  const avgSoh = sohValues.length ? Math.round(sohValues.reduce((a, b) => a + b, 0) / sohValues.length) : null;
  const facts = [
    [onOffer.length, onOffer.length === 1 ? "vůz skladem" : onOffer.length >= 2 && onOffer.length <= 4 ? "vozy skladem" : "vozů skladem"],
    [new Set(onOffer.map((c) => c.brand)).size, "značek"],
    ...(avgSoh ? [[`${avgSoh} %`, "průměrné SOH baterie"]] : []),
  ];

  return (
    <>
    <header className={s.banner}>
      <img src="/hero.jpg" alt="" aria-hidden="true" className={s.bannerImg} />
      <div className={`container ${s.head}`}>
        <p className="eyebrow">Skladem v ČR</p>
        <h1 className={s.title}>Skladové <span>elektromobily</span></h1>
        <p className="lead">Prověřené vozy s ověřeným stavem baterie, připravené k prohlídce a testovací jízdě.</p>
        {carsStatus === "ready" && onOffer.length > 0 && (
          <dl className={s.facts}>
            {facts.map(([v, l]) => <div key={l}><dd className="num"><CountUp value={v} /></dd><dt>{l}</dt></div>)}
          </dl>
        )}
      </div>
    </header>

    <div className="container">
      <form className={s.filters} role="search" onSubmit={(e) => e.preventDefault()}>
        <label className={s.search}>
          <span className="visually-hidden">Hledat</span>
          <Icon name="search" size={18} />
          <input type="search" placeholder="Značka nebo model" value={q} onChange={(e) => set("q", e.target.value)} />
        </label>
        <label>
          <span className={s.label}>Značka</span>
          <select value={brand} onChange={(e) => set("znacka", e.target.value)}>
            <option value="">Všechny značky</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label>
          <span className={s.label}>Cena do</span>
          <select value={max || ""} onChange={(e) => set("max", e.target.value)}>
            <option value="">Bez limitu</option>
            {PRICE_CAPS.map((p) => <option key={p} value={p}>{formatPrice(p)}</option>)}
          </select>
        </label>
        <label>
          <span className={s.label}>Řazení</span>
          <select value={sort} onChange={(e) => set("razeni", e.target.value === "doporucene" ? "" : e.target.value)}>
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
      </form>

      <div className={s.resultBar}>
        <p aria-live="polite"><span className={`num ${s.count}`}>{filtered.length}</span> {filtered.length === 1 ? "vůz" : filtered.length >= 2 && filtered.length <= 4 ? "vozy" : "vozů"}</p>
        {soldCount > 0 && (
          <label className={s.toggle}>
            <input type="checkbox" checked={!hideSold} onChange={(e) => set("prodane", e.target.checked ? "zobrazit" : "")} />
            Zobrazit i prodané ({soldCount})
          </label>
        )}
      </div>

      <div className={s.grid}>
        <CarGridState
          status={carsStatus} count={filtered.length}
          emptyText={cars.length ? "Filtrům neodpovídá žádný vůz." : undefined}
          onReset={cars.length ? () => setParams({}, { replace: true }) : undefined}
        >
          <div className={s.cars}>
            {filtered.map((c) => <CarCard key={c.id} car={c} />)}
          </div>
        </CarGridState>
      </div>
    </div>
    </>
  );
}
